import { Form, FormQuestion, LandingPage } from '../types'
import { AVA_FORM, AVA_QUESTIONS } from '../data/avaForm'
import { AVA_LANDING } from '../data/avaLanding'
import {
  AVA_PILLARS,
  AVA_TEMPLATE_VERSION,
  HIDE_ON_TEMPLATE_VERSION,
  LandingTemplateId,
  templateBlocks,
  templateKeyFor,
} from '../data/landingTemplates'
import { slugify, uid } from '../utils/format'
import { supabase } from './supabaseClient'
import {
  blockRow,
  formConfig,
  formRow,
  landingConfig,
  landingRow,
  questionRow,
} from './mappers'
import { configKey, saveConfig } from './store'

/** Nombre del formulario predeterminado de cada organización. */
export const DEFAULT_FORM_NAME = 'Solicitud de estrategia AVA'

function newId(): string {
  return crypto.randomUUID ? crypto.randomUUID() : uid('id')
}

/**
 * `field_key` de cada pregunta de la plantilla: indica en qué columna de
 * `leads` vuelca su respuesta `submit_public_form`.
 */
const FIELD_KEYS: Record<string, string> = {
  q_ubicacion: 'city',
  q_instagram: 'instagram',
  q_descripcion: 'description',
  q_tipo_servicio: 'service_type',
  q_inversion: 'ad_investment',
  q_presupuesto: 'budget',
  q_comentarios: 'comments',
}

const CONTACT_FIELD_KEYS: Record<string, string> = {
  firstName: 'full_name',
  fullName: 'full_name',
  company: 'company',
  phone: 'phone',
  email: 'email',
}

/** Identifica una pregunta de la plantilla ya existente en la organización. */
function matches(row: { question?: string; field_key?: string }, template: FormQuestion) {
  const expectedKey = FIELD_KEYS[template.id]
  if (expectedKey && row.field_key === expectedKey) return true
  return (row.question ?? '').trim().toLowerCase() === template.label.trim().toLowerCase()
}

/**
 * Garantiza que la organización tiene el formulario predeterminado completo
 * (bloque de contacto + las seis preguntas) y al menos dos landings — con y
 * sin vídeo — asociadas a ese formulario.
 *
 * Es idempotente y no destructivo:
 *  - si el formulario no existe, lo crea entero;
 *  - si existe incompleto, añade SOLO las preguntas que falten al final;
 *  - conserva cualquier pregunta personalizada del usuario;
 *  - no duplica el bloque de contacto ni las landings.
 */
export async function ensureDefaultContent(
  organizationId: string,
  companyName: string,
): Promise<{ formId: string; created: boolean; addedQuestions: number }> {
  const base = slugify(companyName) || 'empresa'

  const { data: forms, error: formsError } = await supabase
    .from('forms')
    .select('*')
    .eq('organization_id', organizationId)
    .is('deleted_at', null)
    .order('created_at')
  if (formsError) {
    throw new Error(`No se han podido leer los formularios: ${formsError.message}`)
  }

  let formId = ''
  let created = false
  let addedQuestions = 0

  const existing =
    (forms ?? []).find(
      (f: { name?: string }) =>
        (f.name ?? '').trim().toLowerCase() === DEFAULT_FORM_NAME.toLowerCase(),
    ) ?? (forms ?? [])[0]

  if (!existing) {
    // No hay ningún formulario: se crea completo.
    const form: Form = {
      ...(JSON.parse(JSON.stringify(AVA_FORM)) as Form),
      id: newId(),
      organizationId,
      name: DEFAULT_FORM_NAME,
      slug: `${base}-solicitud-estrategia`,
      status: 'activo',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    }
    formId = form.id
    created = true

    const { error } = await supabase.from('forms').insert(formRow(form))
    if (error) {
      throw new Error(`No se ha podido crear el formulario: ${error.message}`)
    }

    const rows = AVA_QUESTIONS.map((template, index) => {
      const question: FormQuestion = {
        ...template,
        id: newId(),
        formId: form.id,
        order: index + 1,
      }
      return {
        ...questionRow(question, form.id, organizationId),
        field_key: FIELD_KEYS[template.id] ?? null,
      }
    })
    const { error: questionsError } = await supabase
      .from('form_questions')
      .insert(rows)
    if (questionsError) {
      throw new Error(
        `No se han podido crear las preguntas: ${questionsError.message}`,
      )
    }
    addedQuestions = rows.length

    await saveConfig(organizationId, 'form', form.id, {
      ...formConfig(form),
      contactFieldKeys: CONTACT_FIELD_KEYS,
    })
  } else {
    formId = existing.id

    // Completa las preguntas que falten sin tocar las personalizadas.
    const { data: questionRows, error: readError } = await supabase
      .from('form_questions')
      .select('id, question, field_key, position')
      .eq('form_id', formId)
    if (readError) {
      throw new Error(`No se han podido leer las preguntas: ${readError.message}`)
    }

    const present = questionRows ?? []
    let nextPosition =
      present.reduce(
        (max: number, row: { position?: number }) => Math.max(max, row.position ?? 0),
        0,
      ) + 1

    const missing = AVA_QUESTIONS.filter(
      (template) => !present.some((row) => matches(row, template)),
    )
    if (missing.length) {
      const rows = missing.map((template) => {
        const question: FormQuestion = {
          ...template,
          id: newId(),
          formId,
          order: nextPosition++,
        }
        return {
          ...questionRow(question, formId, organizationId),
          field_key: FIELD_KEYS[template.id] ?? null,
        }
      })
      const { error: insertError } = await supabase
        .from('form_questions')
        .insert(rows)
      if (insertError) {
        throw new Error(
          `No se han podido añadir las preguntas que faltaban: ${insertError.message}`,
        )
      }
      addedQuestions = rows.length
    }

    // Asegura el bloque de contacto y el aviso de privacidad, sin duplicarlos.
    const { data: settings } = await supabase
      .from('organization_settings')
      .select('value')
      .eq('organization_id', organizationId)
      .eq('key', `form_config:${formId}`)
      .maybeSingle()
    const config = (settings?.value ?? {}) as Record<string, unknown>
    const contactFields = config.contactFields as
      | { key: string }[]
      | undefined
    if (contactFields?.some((field) => field.key === 'company')) {
      // El primer paso ya no pide el nombre de la empresa: se deja de mostrar
      // y de exigir, sin tocar la columna `company` de `leads`.
      await saveConfig(organizationId, 'form', formId, {
        ...config,
        contactFields: contactFields.filter((field) => field.key !== 'company'),
      })
    } else if (!contactFields || contactFields.length === 0) {
      await saveConfig(organizationId, 'form', formId, {
        ...config,
        contactFields: AVA_FORM.contactFields,
        contactPosition: 'inicio',
        contactTitle: AVA_FORM.contactTitle,
        contactFieldKeys: CONTACT_FIELD_KEYS,
        submitLabel: config.submitLabel ?? AVA_FORM.submitLabel,
        privacyNotice: config.privacyNotice ?? AVA_FORM.privacyNotice,
        status: config.status ?? 'activo',
      })
    }

    // El formulario predeterminado debe estar activo para poder publicarse.
    if (existing.is_active === false) {
      await supabase.from('forms').update({ is_active: true }).eq('id', formId)
    }
  }

  await ensureDefaultLandings(organizationId, companyName, formId, base)

  return { formId, created, addedQuestions }
}

/**
 * Crea (si faltan) la landing con vídeo y la landing sin vídeo, y asocia el
 * formulario real a todas las landings de la organización que no tengan uno.
 */
async function ensureDefaultLandings(
  organizationId: string,
  companyName: string,
  formId: string,
  base: string,
): Promise<void> {
  const { data: landings, error } = await supabase
    .from('landing_pages')
    .select('id, slug, form_id')
    .eq('organization_id', organizationId)
    .is('deleted_at', null)
    .order('created_at')
  if (error) {
    throw new Error(`No se han podido leer las landings: ${error.message}`)
  }
  const rows = landings ?? []

  const withVideoSlug = base
  const withoutVideoSlug = `${base}-directa`

  if (!rows.some((l: { slug: string }) => l.slug === withVideoSlug)) {
    await createLandingFromTemplate(
      organizationId,
      formId,
      // Landing principal: exactamente el diseño aprobado (caso de éxito).
      `Landing de ${companyName}`,
      withVideoSlug,
      'caso_exito',
    )
  }
  if (!rows.some((l: { slug: string }) => l.slug === withoutVideoSlug)) {
    await createLandingFromTemplate(
      organizationId,
      formId,
      `Landing directa de ${companyName}`,
      withoutVideoSlug,
      'formulario',
    )
  }

  // Landings existentes sin formulario asociado: se conectan al real.
  const orphans = rows
    .filter((l: { form_id: string | null }) => !l.form_id)
    .map((l: { id: string }) => l.id)
  if (orphans.length) {
    await supabase
      .from('landing_pages')
      .update({ form_id: formId })
      .in('id', orphans)
  }

  // Landings anteriores: se completan con los bloques que falten de la
  // plantilla, conservando su contenido y estilos personalizados.
  for (const landing of rows) {
    const template: LandingTemplateId =
      landing.slug === withoutVideoSlug ? 'formulario' : 'caso_exito'
    await migrateLandingBlocks(organizationId, landing.id, template)
  }
}

/**
 * Sincroniza una landing existente con la plantilla oficial SIN sobrescribir
 * nada de lo que el usuario haya personalizado:
 *  - conserva cada bloque existente con su contenido, estilos y visibilidad;
 *  - añade únicamente los bloques de la plantilla que falten;
 *  - respeta los bloques propios que no estén en la plantilla;
 *  - oculta el calendario si está visible pero no tiene enlace de reserva.
 * Es idempotente: si no falta nada, no escribe en la base de datos.
 */
async function migrateLandingBlocks(
  organizationId: string,
  landingId: string,
  template: LandingTemplateId,
): Promise<void> {
  const { data, error } = await supabase
    .from('landing_blocks')
    .select('id, type, position, is_visible, content')
    .eq('landing_id', landingId)
    .order('position')
  if (error) {
    throw new Error(`No se han podido leer los bloques: ${error.message}`)
  }
  const existing = data ?? []
  if (existing.length === 0) return

  await migratePillars(existing)

  /**
   * Restauración única de la plantilla oficial (`ava_original_vsl`, v1): los
   * bloques que no forman parte de la landing original (beneficios,
   * testimonios, preguntas frecuentes y calendario) pasan a estar ocultos.
   * Se ejecuta UNA sola vez por landing, marcándola con `templateVersion`,
   * de modo que si después el usuario decide mostrarlos, ninguna
   * sincronización posterior vuelve a ocultarlos.
   */
  const { data: settings } = await supabase
    .from('organization_settings')
    .select('value')
    .eq('organization_id', organizationId)
    .eq('key', configKey('landing', landingId))
    .maybeSingle()
  const config = (settings?.value ?? {}) as Record<string, unknown>
  const applied = Number(config.templateVersion ?? 0)
  if (applied < AVA_TEMPLATE_VERSION) {
    /*
     * Solo se ocultan los bloques que introduce cada versión NUEVA de la
     * plantilla. Una versión posterior nunca vuelve a ocultar los bloques de
     * una anterior, así que si el usuario decidió mostrar testimonios o
     * preguntas frecuentes, siguen visibles.
     */
    const types = new Set<string>()
    for (let v = applied + 1; v <= AVA_TEMPLATE_VERSION; v += 1) {
      for (const type of HIDE_ON_TEMPLATE_VERSION[v] ?? []) types.add(type)
    }
    const toHide = existing
      .filter(
        (b: { type: string; is_visible: boolean | null }) =>
          b.is_visible !== false && types.has(b.type),
      )
      .map((b: { id: string }) => b.id)
    if (toHide.length) {
      await supabase
        .from('landing_blocks')
        .update({ is_visible: false })
        .in('id', toHide)
    }
    await saveConfig(organizationId, 'landing', landingId, {
      ...config,
      templateKey: templateKeyFor(template),
      templateVersion: AVA_TEMPLATE_VERSION,
    })
  }

  const reference = templateBlocks(template, () => newId())
  const existingTypes = new Set(existing.map((b: { type: string }) => b.type))

  // Orden final: se recorre la plantilla reutilizando el bloque existente de
  // cada tipo y creando solo los ausentes; después van los bloques propios.
  const used = new Set<string>()
  const ordered: { id: string; isNew: boolean; block?: (typeof reference)[number] }[] = []
  for (const block of reference) {
    const match = existing.find(
      (b: { id: string; type: string }) => b.type === block.type && !used.has(b.id),
    )
    if (match) {
      used.add(match.id)
      ordered.push({ id: match.id, isNew: false })
    } else {
      ordered.push({ id: block.id, isNew: true, block })
    }
  }
  // Los bloques propios que no están en la plantilla se conservan justo antes
  // del footer, para no dejarlos por debajo del pie de página.
  const extras = existing
    .filter((b: { id: string }) => !used.has(b.id))
    .map((b: { id: string }) => ({ id: b.id, isNew: false }))
  if (extras.length) {
    const footerIndex = ordered.findIndex((entry) => {
      const type = entry.isNew
        ? entry.block!.type
        : existing.find((b: { id: string }) => b.id === entry.id)?.type
      return type === 'footer'
    })
    if (footerIndex === -1) ordered.push(...extras)
    else ordered.splice(footerIndex, 0, ...extras)
  }

  const missing = ordered.filter((entry) => entry.isNew)

  // Calendario visible sin enlace: se oculta para no dejar un hueco.
  const brokenCalendars = existing.filter((b: {
    type: string
    is_visible: boolean | null
    content: Record<string, unknown> | null
  }) => {
    if (b.type !== 'calendario' || b.is_visible === false) return false
    const content = (b.content ?? {}) as Record<string, unknown>
    const url = String(content.url ?? content.globalUrl ?? '').trim()
    return url.length === 0
  })

  if (missing.length === 0 && brokenCalendars.length === 0) return

  if (missing.length > 0) {
    const { error: insertError } = await supabase.from('landing_blocks').insert(
      missing.map((entry) =>
        blockRow(
          entry.block!,
          landingId,
          organizationId,
          ordered.findIndex((o) => o.id === entry.id),
        ),
      ),
    )
    if (insertError) {
      throw new Error(
        `No se han podido añadir los bloques que faltaban: ${insertError.message}`,
      )
    }
    // Reposiciona los bloques existentes para respetar el orden de la plantilla.
    for (let index = 0; index < ordered.length; index += 1) {
      const entry = ordered[index]
      if (entry.isNew) continue
      await supabase
        .from('landing_blocks')
        .update({ position: index })
        .eq('id', entry.id)
    }
  }

  for (const calendar of brokenCalendars) {
    await supabase
      .from('landing_blocks')
      .update({ is_visible: false })
      .eq('id', calendar.id)
  }
}

/**
 * Compatibilidad de los tres pilares en landings ya publicadas.
 *
 * Antes de existir el bloque `pilares`, Atraemos · Viralizamos · Automatizamos
 * se creaba como bloque `beneficios`, que en móvil se apila. Esta migración,
 * idempotente y sin tocar los textos de escritorio:
 *  - convierte a `pilares` los bloques `beneficios` cuyos elementos son los
 *    tres pilares (comparando por título);
 *  - fija «una única fila» como disposición móvil si no está definida;
 *  - rellena el texto corto de móvil que falte.
 */
async function migratePillars(
  blocks: {
    id: string
    type: string
    content: Record<string, unknown> | null
  }[],
): Promise<void> {
  const shortTexts: Record<string, string> = {}
  for (const pillar of AVA_PILLARS) {
    shortTexts[pillar.title.trim().toLowerCase()] = pillar.mobileText
  }
  const pillarTitles = Object.keys(shortTexts)
  // Si la landing ya tiene un bloque `pilares`, no se convierte ninguno más:
  // así nunca aparece la sección duplicada.
  let hasPillars = blocks.some((b) => b.type === 'pilares')

  for (const block of blocks) {
    if (block.type !== 'pilares' && block.type !== 'beneficios') continue
    const content = { ...((block.content ?? {}) as Record<string, unknown>) }
    const items = Array.isArray(content.items)
      ? (content.items as Record<string, unknown>[])
      : []
    if (items.length !== 3) continue

    const titles = items.map((item) => String(item.title ?? '').trim().toLowerCase())
    const isPillarSet = titles.every((title) => pillarTitles.includes(title))
    // Un bloque `beneficios` solo se convierte si son exactamente los tres
    // pilares; cualquier otro bloque de beneficios se deja intacto.
    if (block.type === 'beneficios' && !isPillarSet) continue

    let changed = false
    const nextItems = items.map((item, index) => {
      const key = String(item.title ?? '').trim().toLowerCase()
      const short = shortTexts[key] ?? AVA_PILLARS[index]?.mobileText
      if (String(item.mobileText ?? '').trim() || !short) return item
      changed = true
      return { ...item, mobileText: short }
    })
    if (changed) content.items = nextItems
    if (content.mobileLayout === undefined) {
      content.mobileLayout = 'fila'
      changed = true
    }
    if (content.showMobileText === undefined) {
      content.showMobileText = 'si'
      changed = true
    }
    const becomesPillars = block.type === 'beneficios' && isPillarSet && !hasPillars
    if (!changed && !becomesPillars) continue

    const update: Record<string, unknown> = { content }
    if (becomesPillars) update.type = 'pilares'
    const { error } = await supabase
      .from('landing_blocks')
      .update(update)
      .eq('id', block.id)
    if (error) {
      throw new Error(
        `No se han podido actualizar los tres pilares: ${error.message}`,
      )
    }
    if (becomesPillars) {
      block.type = 'pilares'
      hasPillars = true
    }
    block.content = content
  }
}

/** Crea una landing a partir de una de las dos plantillas por defecto. */
async function createLandingFromTemplate(
  organizationId: string,
  formId: string,
  name: string,
  slug: string,
  template: LandingTemplateId,
): Promise<void> {
  const landing: LandingPage = {
    ...(JSON.parse(JSON.stringify(AVA_LANDING)) as LandingPage),
    id: newId(),
    organizationId,
    formId,
    name,
    slug,
    status: 'publicada',
    views: 0,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  }

  const blocks = templateBlocks(template, () => newId())
  landing.pageConfig = { ...landing.pageConfig, blocks }

  const { error } = await supabase
    .from('landing_pages')
    .insert(landingRow(landing))
  if (error) {
    throw new Error(`No se ha podido crear la landing: ${error.message}`)
  }

  const { error: blocksError } = await supabase
    .from('landing_blocks')
    .insert(
      blocks.map((block, index) =>
        // La copia pública de la configuración viaja con los bloques: es lo
        // único que un visitante anónimo puede leer.
        blockRow(
          block,
          landing.id,
          organizationId,
          index,
          landingConfig(landing),
        ),
      ),
    )
  if (blocksError) {
    throw new Error(
      `No se han podido crear los bloques de la landing: ${blocksError.message}`,
    )
  }

  await saveConfig(organizationId, 'landing', landing.id, landingConfig(landing))
}
